// src/components/agent/parts/MarkdownBlock.js
//
// The ReactMarkdown style wrapper, extracted from MessageDisplay.js (frontend
// spec §8.6). Two surfaces: user bubbles keep v1's on-primary styling
// (colour inherited, white-alpha accents), while assistant content is
// re-based on the page surface with theme-aware colours — code/table
// backgrounds work in BOTH light and dark modes.
import React from 'react';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// GitHub-flavoured Markdown: pipe tables, bare-URL autolinks, strikethrough and
// task lists — the forms the model actually emits. One array, so the parser
// configuration is referentially stable across renders.
const REMARK_PLUGINS = [remarkGfm];

const StyledMarkdown = styled(ReactMarkdown, {
  shouldForwardProp: (prop) => prop !== 'userBubble',
})(({ theme, userBubble }) => {
  const isDark = theme.palette.mode === 'dark';
  // On the primary-blue user bubble the v1 alphas read correctly in both
  // theme modes; on the page surface the accents derive from the mode.
  const codeBg = userBubble
    ? 'rgba(0, 0, 0, 0.15)'
    : (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)');
  const tableBorder = userBubble
    ? 'rgba(255, 255, 255, 0.3)'
    : theme.palette.divider;

  return {
    color: 'inherit',
    fontSize: theme.typography.body1.fontSize,
    fontFamily: theme.typography.fontFamily,
    wordWrap: 'break-word',
    '& > *:first-of-type': { marginTop: 0 },
    '& > *:last-of-type': { marginBottom: 0 },
    '& p': { margin: theme.spacing(0, 0, 1, 0) },
    '& a': { color: userBubble ? 'inherit' : theme.palette.primary.main, textDecoration: 'underline' },
    '& code:not(pre code)': {
      backgroundColor: codeBg,
      padding: '0.2em 0.4em',
      borderRadius: '3px',
      fontFamily: 'monospace',
      fontSize: '85%',
    },
    '& pre': {
      backgroundColor: codeBg,
      padding: theme.spacing(1),
      borderRadius: '4px',
      overflowX: 'auto',
      fontFamily: 'monospace',
      margin: theme.spacing(1, 0),
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
      fontSize: '100%',
      fontFamily: 'inherit',
    },
    '& ul, & ol': { paddingLeft: theme.spacing(3), margin: theme.spacing(1, 0) },
    '& li': { marginBottom: theme.spacing(0.5) },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: theme.spacing(1, 0),
      border: `1px solid ${tableBorder}`,
    },
    '& th, & td': {
      border: `1px solid ${tableBorder}`,
      padding: theme.spacing(0.5, 1),
      textAlign: 'left',
    },
    '& th': { fontWeight: 'bold' },
    '& blockquote': {
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(1.5),
      borderLeft: `3px solid ${tableBorder}`,
      opacity: 0.9,
    },
  };
});

function MarkdownBlock({ text, userBubble = false }) {
  return <StyledMarkdown userBubble={userBubble} remarkPlugins={REMARK_PLUGINS}>{text || ''}</StyledMarkdown>;
}

export default MarkdownBlock;
